import {useProjectHasTraceServerData} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import {useGetTraceServerClientContext} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientContext';
import {projectIdFromParts} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/tsDataModelHooks';
import {useContext, useEffect, useState} from 'react';

import {RunQueryContext} from '../state/runs/context';

/**
 * Hook that checks if weave trace panel should be included based on:
 * 1. Feature flag (weave_panels_in_workspace)
 * 2. Project has trace server data
 * 3. Project has calls with wb_run_id (expensive check)
 */
export const useCheckWeaveSignal = (): boolean => {
  const {entityName, projectName} = useContext(RunQueryContext);

  // by default we don't include the weave trace panel
  const [shouldIncludeWeavePanel, setShouldIncludeWeavePanel] =
    useState<boolean>(false);

  const projectHasTraceData = useProjectHasTraceServerData(
    entityName ?? '',
    projectName ?? ''
  );
  const getTsClient = useGetTraceServerClientContext();

  useEffect(() => {
    // Early exit if feature flag is loading or disabled
    if (projectHasTraceData.loading) {
      return;
    }

    // If the project has no trace server data, we can't show the weave signal
    if (!projectHasTraceData.result) {
      return;
    }

    (async () => {
      const res = await getTsClient().callsQueryStats({
        project_id: projectIdFromParts({
          entity: entityName ?? '',
          project: projectName ?? '',
        }),
        // set limit to 1 to avoid unnecessary data reads
        limit: 1,
        query: {
          $expr: {
            $and: [
              // Filter for non-null wb_run_id
              {
                $not: [
                  {
                    $eq: [{$getField: 'wb_run_id'}, {$literal: null}],
                  },
                ],
              },
            ],
          },
        },
      });

      setShouldIncludeWeavePanel(res.count > 0);
    })();
  }, [
    entityName,
    getTsClient,
    projectName,
    projectHasTraceData.loading,
    projectHasTraceData.result,
  ]);

  return shouldIncludeWeavePanel;
};
